import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle2, MessageCircle, Building2, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { BRAND } from '../data/mock';
import { Reveal, SplitText, Magnetic, AnimatedDivider } from '../components/effects';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({
    company: '', name: '', title: '', phone: '', email: '',
    headcount: '', service: 'Taşıma Yemek Hizmeti', city: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.name || !form.phone) {
      toast({ title: 'Eksik bilgi', description: 'Şirket, isim ve telefon zorunludur.' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source: 'contact-form' }),
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      setSubmitted(true);
      toast({ title: 'Talebiniz alındı', description: 'Müşteri yöneticimiz aynı gün içinde sizi arayacak.' });
    } catch (err) {
      console.error('lead submit failed', err);
      toast({
        title: 'Bağlantı hatası',
        description: 'Lütfen birkaç dakika sonra tekrar deneyin veya WhatsApp üzerinden yazın.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <HeroBlock />

      <section className="py-20 lg:py-28 bg-[#EFEBE3]">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="bg-[#F5F3EF] p-5 sm:p-8 lg:p-12 rounded-sm border border-[#9A7F4F]/22 shadow-[0_30px_80px_-40px_rgba(114,39,45,0.30)]">
                {!submitted ? (
                  <>
                    <div className="flex items-center gap-3 mb-9">
                      <span className="text-[10.5px] numeric tracking-[0.32em] text-[#9A7F4F] font-medium">
                        01
                      </span>
                      <span className="h-px w-10 bg-[#9A7F4F]/55" />
                      <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#1A1A1A]/65 font-medium">
                        Teklif Formu
                      </span>
                    </div>

                    <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <Field label="Şirket Adı *" name="company" value={form.company} onChange={handleChange} testid="contact-input-company" />
                      <Field label="Sektör / Ünvan" name="title" value={form.title} onChange={handleChange} placeholder="Otomotiv / İK Müdürü" testid="contact-input-title" />
                      <Field label="Yetkili Adı *" name="name" value={form.name} onChange={handleChange} testid="contact-input-name" />
                      <Field label="Telefon *" name="phone" value={form.phone} onChange={handleChange} placeholder="0 5XX XXX XX XX" testid="contact-input-phone" />
                      <Field label="E-posta" name="email" type="email" value={form.email} onChange={handleChange} testid="contact-input-email" />
                      <Field label="Şehir / İlçe" name="city" value={form.city} onChange={handleChange} placeholder="Kocaeli / Gebze" testid="contact-input-city" />

                      <div>
                        <label className="block text-[10.5px] tracking-[0.28em] uppercase text-[#1A1A1A]/65 font-medium mb-2.5">İlgilendiğiniz Hizmet</label>
                        <div
                          className="w-full bg-[#F5F3EF] border border-[#9A7F4F]/30 px-4 py-3 text-[14px] text-[#1A1A1A] rounded-sm flex items-center justify-between"
                          data-testid="contact-service-readonly"
                        >
                          <span>Taşıma Yemek Hizmeti</span>
                          <span className="text-[10px] tracking-[0.28em] uppercase text-[#9A7F4F] font-medium">
                            Tek Hizmet
                          </span>
                        </div>
                      </div>
                      <SelectField label="Yaklaşık Kişi Sayısı" name="headcount" value={form.headcount} onChange={handleChange}
                        options={['50-150', '150-500', '500-1.000', '1.000-2.500', '2.500+']}
                        testid="contact-select-headcount" />

                      <div className="sm:col-span-2">
                        <label className="block text-[10.5px] tracking-[0.28em] uppercase text-[#1A1A1A]/65 font-medium mb-2.5">Notlar</label>
                        <textarea
                          name="message"
                          value={form.message}
                          onChange={handleChange}
                          rows={4}
                          placeholder="Vardiya saatleriniz, özel diyet ihtiyaçlarınız, başlamak istediğiniz tarih..."
                          className="w-full bg-white border border-[#1A1A1A]/[0.12] px-4 py-3 text-[14px] text-[#1A1A1A] placeholder:text-[#1A1A1A]/35 focus:outline-none focus:border-[#9A7F4F]/70 transition-colors rounded-sm"
                          data-testid="contact-input-message"
                        />
                      </div>

                      <div className="sm:col-span-2 flex flex-wrap items-center gap-5 pt-3">
                        <Magnetic strength={0.18}>
                          <button
                            type="submit"
                            disabled={submitting}
                            data-testid="contact-submit-btn"
                            className="group inline-flex items-center justify-center gap-3 bg-[#1A1A1A] text-[#F5F3EF] px-6 sm:px-8 py-[16px] sm:py-[18px] text-[11px] sm:text-[12px] tracking-[0.18em] sm:tracking-[0.22em] uppercase font-medium rounded-sm transition-colors duration-300 hover:bg-[#9A7F4F] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-[#1A1A1A] whitespace-nowrap"
                          >
                            {submitting ? (
                              <>
                                <Loader2 size={14} className="animate-spin" />
                                Gönderiliyor
                              </>
                            ) : (
                              <>
                                Teklif Talebimi Gönder
                                <Send size={14} className="transition-transform group-hover:translate-x-1" />
                              </>
                            )}
                          </button>
                        </Magnetic>
                        <a
                          href={BRAND.whatsapp}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 text-[#1A1A1A] text-[12.5px] tracking-[0.18em] uppercase font-medium border-b border-[#1A1A1A]/30 pb-1.5 hover:border-[#9A7F4F] hover:text-[#9A7F4F] transition-colors"
                          data-testid="contact-whatsapp-btn"
                        >
                          <MessageCircle size={13} className="text-[#9A7F4F]" /> WhatsApp ile İletişim
                        </a>
                      </div>
                      <p className="sm:col-span-2 text-[11px] text-[#1A1A1A]/50 mt-2">
                        Bilgileriniz KVKK kapsamında korunur, üçüncü taraflarla paylaşılmaz.
                      </p>
                    </form>
                  </>
                ) : (
                  <div className="py-16 text-center">
                    <div className="h-20 w-20 mx-auto rounded-full bg-[#F5F0E5] grid place-items-center border border-[#9A7F4F]/45">
                      <CheckCircle2 size={40} strokeWidth={1.4} className="text-[#9A7F4F]" />
                    </div>
                    <h3
                      className="font-display text-[34px] lg:text-[40px] text-[#1A1A1A] mt-7 italic"
                      style={{ fontWeight: 400 }}
                    >
                      Talebiniz alındı.
                    </h3>
                    <p className="text-[#1A1A1A]/68 mt-4 text-[14.5px] leading-[1.7] max-w-md mx-auto">
                      Müşteri yöneticimiz aynı gün içinde sizi arayacaktır.
                      <br />
                      Acil durumda{' '}
                      <a
                        href={BRAND.whatsapp}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#9A7F4F] border-b border-[#9A7F4F]/40 hover:border-[#9A7F4F] transition-colors"
                      >
                        WhatsApp
                      </a>{' '}
                      üzerinden de yazabilirsiniz.
                    </p>
                  </div>
                )}
              </div>
            </Reveal>
          </div>

          {/* Info */}
          <aside className="lg:col-span-5 space-y-3.5">
            <InfoCard Icon={Phone} title="Telefon" line1={BRAND.phone} sub="Pazartesi—Cumartesi · 07:00 — 22:00" href={`tel:${BRAND.phoneRaw}`} />
            <InfoCard Icon={Phone} title="Telefon" line1={BRAND.fax} sub="Kurumsal hat" />
            <InfoCard Icon={Mail} title="E-posta" line1={BRAND.email} sub="Genellikle 2 saat içinde dönüş" href={`mailto:${BRAND.email}`} />
            <InfoCard Icon={MapPin} title="Merkez & Mutfak" line1={BRAND.address} sub="3.200 m² HACCP sertifikalı tesis" />
            <InfoCard Icon={Building2} title="Hizmet Bölgemiz" line1="Kocaeli · İstanbul · Sakarya" sub="Yalova · Bursa" />
          </aside>
        </div>
      </section>

      <Toaster />
    </>
  );
};

const HeroBlock = () => (
  <section className="relative pt-[170px] lg:pt-[200px] pb-16 lg:pb-24 bg-[#F5F3EF] overflow-hidden">
    <div className="container-x relative">
      <div className="flex items-center gap-3 mb-7">
        <AnimatedDivider width="3rem" />
        <Reveal delay={100} y={8} duration={700}>
          <span className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium">
            İletişim & Teklif
          </span>
        </Reveal>
      </div>

      <h1
        className="font-display text-[44px] sm:text-[64px] lg:text-[88px] leading-[0.98] tracking-[-0.022em] text-[#1A1A1A] max-w-5xl"
        style={{ fontWeight: 500 }}
      >
        <SplitText text="48 saat içinde" delay={120} />
        <br />
        <span className="italic font-light text-[#9A7F4F]">
          <SplitText text="size özel teklif." delay={520} />
        </span>
      </h1>

      <Reveal delay={1000}>
        <p className="mt-9 text-[16px] lg:text-[18px] text-[#1A1A1A]/70 max-w-2xl leading-[1.7]">
          Aşağıdaki formu doldurun; müşteri yöneticimiz aynı gün sizi arasın, ihtiyacınızı
          dinlesin ve sahanıza özel bir teklif hazırlasın.
        </p>
      </Reveal>
    </div>
  </section>
);

const Field = ({ label, name, value, onChange, type = 'text', placeholder = '', testid }) => (
  <div>
    <label className="block text-[10.5px] tracking-[0.28em] uppercase text-[#1A1A1A]/65 font-medium mb-2.5">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full bg-white border border-[#1A1A1A]/[0.12] px-4 py-3 text-[14px] text-[#1A1A1A] placeholder:text-[#1A1A1A]/35 focus:outline-none focus:border-[#9A7F4F]/70 transition-colors rounded-sm"
    />
  </div>
);

const SelectField = ({ label, name, value, onChange, options, testid }) => (
  <div>
    <label className="block text-[10.5px] tracking-[0.28em] uppercase text-[#1A1A1A]/65 font-medium mb-2.5">
      {label}
    </label>
    <select
      name={name}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="w-full bg-white border border-[#1A1A1A]/[0.12] px-4 py-3 text-[14px] text-[#1A1A1A] focus:outline-none focus:border-[#9A7F4F]/70 transition-colors rounded-sm"
    >
      <option value="">Seçiniz</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  </div>
);

const InfoCard = ({ Icon, title, line1, sub, href }) => {
  const Wrapper = href ? 'a' : 'div';
  return (
    <Wrapper
      href={href}
      className="bg-[#F5F3EF] border border-[#1A1A1A]/[0.08] p-6 lg:p-7 flex items-start gap-5 hover:border-[#9A7F4F]/45 transition-colors rounded-sm group"
    >
      <div className="h-12 w-12 grid place-items-center border border-[#9A7F4F]/45 text-[#9A7F4F] shrink-0 group-hover:bg-[#9A7F4F] group-hover:text-[#F5F3EF] transition-colors">
        <Icon size={18} strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10.5px] tracking-[0.32em] uppercase text-[#9A7F4F] font-medium mb-1.5">
          {title}
        </div>
        <div className="text-[#1A1A1A] text-[14px] sm:text-[15px] font-medium break-words">{line1}</div>
        <div className="text-[#1A1A1A]/55 text-[12.5px] mt-1">{sub}</div>
      </div>
    </Wrapper>
  );
};

export default Contact;
